import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Download, Copy, Check, Share2, MessageCircle, Send, Twitter, Facebook } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

/* Branded social share modal: preview card + WhatsApp/Telegram/X/Facebook/native share. */
export function SocialShareModal({ open, onClose, buildCanvas, message, url, filename = "hikmahverses-card.png" }) {
  const { tx, t } = useLanguage();
  const holderRef = useRef(null);
  const canvasRef = useRef(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !holderRef.current) return;
    const canvas = buildCanvas();
    canvasRef.current = canvas;
    canvas.style.width = "100%";
    canvas.style.height = "auto";
    canvas.style.borderRadius = "16px";
    holderRef.current.innerHTML = "";
    holderRef.current.appendChild(canvas);
  }, [open, buildCanvas]);

  if (!open) return null;

  const fullText = `${message}\n${url}`;
  const enc = encodeURIComponent;
  const links = [
    { label: "WhatsApp", icon: MessageCircle, href: `https://wa.me/?text=${enc(fullText)}`, cls: "bg-[#25D366] text-white hover:opacity-90", testid: "share-whatsapp" },
    { label: "Telegram", icon: Send, href: `https://t.me/share/url?url=${enc(url)}&text=${enc(message)}`, cls: "bg-[#229ED9] text-white hover:opacity-90", testid: "share-telegram" },
    { label: "X", icon: Twitter, href: `https://twitter.com/intent/tweet?text=${enc(fullText)}`, cls: "bg-black text-white hover:opacity-90", testid: "share-x" },
    { label: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}&quote=${enc(message)}`, cls: "bg-[#1877F2] text-white hover:opacity-90", testid: "share-facebook" },
  ];

  const download = () => {
    const a = document.createElement("a");
    a.download = filename;
    a.href = canvasRef.current.toDataURL("image/png");
    a.click();
    toast.success(t("socialShare.cardDownloadedPerfectForInstagram"));
  };

  const copyText = async () => {
    await navigator.clipboard.writeText(fullText);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const nativeShare = async () => {
    try {
      const blob = await new Promise((res) => canvasRef.current.toBlob(res, "image/png"));
      const file = new File([blob], filename, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: fullText });
      } else if (navigator.share) {
        await navigator.share({ text: fullText, url });
      } else {
        download();
      }
    } catch { /* user cancelled */ }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="share-modal">
      <div className="bg-sand-100 rounded-2xl max-w-md w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display font-semibold text-xl text-forest-700">{t("socialShare.shareInspire")}</h3>
          <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-sand-200 grid place-items-center" data-testid="share-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div ref={holderRef} className="mt-4" data-testid="share-card-preview" />
        <div className="mt-4 grid grid-cols-2 gap-2">
          {links.map((l) => (
            <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer"
              className={`rounded-xl px-4 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 transition ${l.cls}`}
              data-testid={l.testid}>
              <l.icon className="h-4 w-4" /> {tx(l.label)}
            </a>
          ))}
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2">
          <button onClick={download} className="btn-ghost text-xs justify-center" data-testid="share-download">
            <Download className="h-3.5 w-3.5" /> {t("socialShare.image")}
          </button>
          <button onClick={copyText} className="btn-ghost text-xs justify-center" data-testid="share-copy">
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied ? t("achievements.copied") : t("socialShare.copyText")}
          </button>
          <button onClick={nativeShare} className="btn-ghost text-xs justify-center" data-testid="share-native">
            <Share2 className="h-3.5 w-3.5" /> {t("socialShare.more")}
          </button>
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground text-center">
          {t("socialShare.forInstagramDownloadTheImage")}
        </p>
      </div>
    </div>
  );
}
